import { describeDevice } from "@breenova/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { api } from "@/lib/api";
import { problemMessage } from "@/lib/birds";

export const pushKeys = {
  all: ["push"] as const,
  settings: ["push", "settings"] as const,
  devices: ["push", "devices"] as const,
};

/** Whether this browser can do push at all, before any question of permission. */
export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * The VAPID key as the Push API wants it: raw bytes, not base64url.
 *
 * `applicationServerKey` is one of the few places in the platform that refuses a string.
 */
function decodeKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));

  // Backed by an explicit ArrayBuffer, not `Uint8Array.from`. Since TypeScript 5.7 the
  // typed arrays are generic over their buffer, and the inferred `ArrayBufferLike` is
  // wider than the `BufferSource` the Push API accepts — it admits a SharedArrayBuffer.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

/** Whether push is switched on for this server, and the key to subscribe with. */
export function usePushSettings() {
  return useQuery({
    queryKey: pushKeys.settings,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/push/settings");

      if (error || !data) {
        throw new Error("Could not check whether notifications are available.");
      }

      return data;
    },
  });
}

export function usePushDevices() {
  return useQuery({
    queryKey: pushKeys.devices,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/push/subscriptions");

      if (error || !data) {
        throw new Error("Could not load your notification devices.");
      }

      return data.devices;
    },
  });
}

export function useRemovePushDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE("/api/v1/push/subscriptions/{id}", {
        params: { path: { id } },
      });

      if (error) {
        throw new Error(problemMessage(error, "Could not remove that device."));
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pushKeys.all }),
  });
}

export function useSendTestPush() {
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST("/api/v1/push/test", {});

      if (error || !data) {
        throw new Error(problemMessage(error, "Could not send a test notification."));
      }

      return data;
    },
  });
}

/**
 * Hands this browser's subscription to the server.
 *
 * Separate from the enable flow because it runs on its own too: the browser can rotate a
 * subscription without telling the page, so the app re-sends whatever it currently has
 * every time it starts. The endpoint is the key on the server side, so re-sending an
 * unchanged one is a no-op rather than a duplicate.
 */
async function register(subscription: globalThis.PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const keys = json.keys ?? {};

  if (!json.endpoint || !keys.p256dh || !keys.auth) {
    throw new Error("This browser gave back an incomplete subscription.");
  }

  const { error } = await api.POST("/api/v1/push/subscriptions", {
    body: {
      endpoint: json.endpoint,
      p256dh: keys.p256dh,
      auth: keys.auth,
      // The same label the security screen puts on a session, so one device is named the
      // same way in both lists. Coalesced because `exactOptionalPropertyTypes` is on and
      // the generated body distinguishes "no label" from "the property is missing".
      deviceLabel: describeDevice() ?? null,
    },
  });

  if (error) {
    throw new Error(problemMessage(error, "Could not register this device."));
  }
}

export type PushPermission = "default" | "granted" | "denied" | "unsupported";

/**
 * Notifications, as one control.
 *
 * The states this has to distinguish are not the same as the browser's, and conflating
 * them is what makes notification settings confusing everywhere they are confusing:
 *
 *   `unsupported` — this browser cannot. On iOS that also means the app is open in a
 *   Safari tab rather than installed, because Safari exposes the Push API only to a
 *   home-screen app. The screen says so, since the fix is a different thing entirely.
 *
 *   `default` — never asked. The prompt is one-shot per site, so it is spent on a
 *   deliberate tap and never on page load.
 *
 *   `denied` — asked and refused, and the browser will not ask again. Nothing the page
 *   does can reopen it; only the browser's own site settings can, which is what the
 *   screen has to say instead of showing a button that cannot work.
 *
 *   `granted` — permission is held. That is still not the same as being subscribed:
 *   permission survives an uninstall and a cleared service worker, both of which take the
 *   subscription with them. So the two are tracked separately.
 */
export function usePushRegistration(vapidPublicKey: string | null | undefined): {
  permission: PushPermission;
  subscribed: boolean;
  endpoint: string | null;
  busy: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
} {
  const queryClient = useQueryClient();

  const [permission, setPermission] = useState<PushPermission>(() =>
    pushSupported() ? Notification.permission : "unsupported",
  );
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const readExisting = useCallback(async (): Promise<globalThis.PushSubscription | null> => {
    if (!pushSupported()) {
      return null;
    }

    const registration = await navigator.serviceWorker.getRegistration();

    return (await registration?.pushManager.getSubscription()) ?? null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    void readExisting().then((subscription) => {
      if (!cancelled) {
        setEndpoint(subscription?.endpoint ?? null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [readExisting]);

  /*
   * The worker asks for this when the browser retires a subscription on its own — a key
   * rotation, a long silence. The worker cannot re-subscribe: it has no VAPID key. The
   * page does, so it is the page that repairs it.
   */
  useEffect(() => {
    if (!pushSupported() || !vapidPublicKey) {
      return;
    }

    const onMessage = (event: MessageEvent): void => {
      if (event.data?.type !== "breenova:resubscribe") {
        return;
      }

      void (async () => {
        const registration = await navigator.serviceWorker.ready;

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: decodeKey(vapidPublicKey),
        });

        await register(subscription);
        setEndpoint(subscription.endpoint);
      })();
    };

    navigator.serviceWorker.addEventListener("message", onMessage);

    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [vapidPublicKey]);

  async function enable(): Promise<void> {
    if (!pushSupported()) {
      throw new Error("This browser cannot show notifications.");
    }

    if (!vapidPublicKey) {
      throw new Error("Notifications are not switched on for this server yet.");
    }

    setBusy(true);

    try {
      const granted = await Notification.requestPermission();
      setPermission(granted);

      if (granted !== "granted") {
        // Not an error to report as a failure: refusing is a legitimate answer, and the
        // screen already reads the permission state back.
        return;
      }

      // `ready` rather than `getRegistration`: the worker may still be installing on a
      // first visit, and subscribing against a worker that is not yet active throws.
      const registration = await navigator.serviceWorker.ready;

      const existing = await registration.pushManager.getSubscription();

      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          // Required, and enforced: a push handled without showing anything costs the
          // site its permission on every browser that supports this.
          userVisibleOnly: true,
          applicationServerKey: decodeKey(vapidPublicKey),
        }));

      await register(subscription);
      setEndpoint(subscription.endpoint);

      void queryClient.invalidateQueries({ queryKey: pushKeys.all });
    } finally {
      setBusy(false);
    }
  }

  async function disable(): Promise<void> {
    setBusy(true);

    try {
      const subscription = await readExisting();

      if (subscription) {
        const dead = subscription.endpoint;

        // Both halves, and in this order. Unsubscribing first means that if the server
        // call fails the browser is already quiet, which is what the breeder asked for —
        // the stale row is then cleaned up by the first send that gets a 410.
        await subscription.unsubscribe();

        const devices = await api.GET("/api/v1/push/subscriptions");
        const match = devices.data?.devices.find((device) => device.endpoint === dead);

        if (match) {
          await api.DELETE("/api/v1/push/subscriptions/{id}", {
            params: { path: { id: match.id } },
          });
        }
      }

      setEndpoint(null);
      void queryClient.invalidateQueries({ queryKey: pushKeys.all });
    } finally {
      setBusy(false);
    }
  }

  return {
    permission,
    subscribed: endpoint !== null,
    endpoint,
    busy,
    enable,
    disable,
  };
}
